// Provider Availability Service
// Handles checking provider availability for specific dates and times

import { supabase } from './supabase';
import { logger } from './logger';

export const providerAvailabilityService = {
  /**
   * Check if multiple providers are available on a specific date
   * @param {Array<string>} providerIds - Array of provider UUIDs to check
   * @param {Date|string} targetDate - The date to check availability for
   * @returns {Promise<Array>} Array of available providers with their time slots
   */
  async checkProvidersAvailability(providerIds, targetDate) {
    try {
      logger.debug('ProviderAvailabilityService: Checking availability', {
        providerCount: providerIds.length,
        targetDate
      });

      if (!providerIds || providerIds.length === 0) {
        return [];
      }

      // Convert targetDate to Date object if it's a string
      const dateObj = typeof targetDate === 'string' ? new Date(targetDate) : targetDate;
      const dayOfWeek = this._getDayOfWeekKey(dateObj);
      const dateString = this._formatDateString(dateObj);

      logger.debug('ProviderAvailabilityService: Parsed date', { dayOfWeek, dateString });

      // Fetch availability data for all providers
      const { data: availabilityRecords, error } = await supabase
        .from('provider_availability')
        .select('provider_id, weekly_availability, blocked_periods, meeting_preferences')
        .in('provider_id', providerIds);

      if (error) {
        logger.error('Error fetching provider availability', { error: error.message });
        throw error;
      }

      if (!availabilityRecords || availabilityRecords.length === 0) {
        logger.debug('ProviderAvailabilityService: No availability records found');
        return [];
      }

      // Also fetch provider profile data including location
      const { data: providers, error: providerError } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, profile_picture, specialty, location')
        .in('id', providerIds);

      if (providerError) {
        logger.warn('Error fetching provider profiles', { error: providerError.message });
      }

      // Create a map of provider data
      const providerMap = {};
      if (providers) {
        providers.forEach(provider => {
          providerMap[provider.id] = provider;
        });
      }

      // Collect all unique location IDs from availability slots
      const locationIds = new Set();
      availabilityRecords.forEach(record => {
        const weeklyAvailability = record.weekly_availability || {};
        Object.values(weeklyAvailability).forEach(daySlots => {
          if (Array.isArray(daySlots)) {
            daySlots.forEach(slot => {
              if (slot.location_id) {
                locationIds.add(slot.location_id);
              }
            });
          }
        });
      });

      logger.debug('ProviderAvailabilityService: Collected location IDs', {
        locationIdsCount: locationIds.size,
        locationIds: Array.from(locationIds)
      });

      // Fetch location data if there are any location IDs
      let locationMap = {};
      if (locationIds.size > 0) {
        const { data: locations, error: locError } = await supabase
          .from('practice_locations')
          .select('id, location_name, address, city, state, location_type')
          .in('id', Array.from(locationIds));

        if (locError) {
          logger.warn('Error fetching practice locations', { error: locError.message });
        }

        if (locations) {
          locations.forEach(loc => {
            locationMap[loc.id] = loc;
          });
        }
      }

      // Also get existing meetings for the date to check conflicts
      const { data: existingMeetings, error: meetingsError } = await supabase
        .from('meetings')
        .select('customer_id, start_time, end_time, status')
        .in('customer_id', providerIds)
        .eq('meeting_date', dateString)
        .in('status', ['pending', 'confirmed']);

      if (meetingsError) {
        logger.warn('Error fetching existing meetings', { error: meetingsError.message });
      }

      // Create a map of existing meetings by provider
      const meetingsByProvider = {};
      if (existingMeetings) {
        existingMeetings.forEach(meeting => {
          if (!meetingsByProvider[meeting.customer_id]) {
            meetingsByProvider[meeting.customer_id] = [];
          }
          meetingsByProvider[meeting.customer_id].push({
            startTime: meeting.start_time,
            endTime: meeting.end_time,
            status: meeting.status
          });
        });
      }

      // Process each provider's availability
      const availableProviders = [];

      availabilityRecords.forEach(record => {
        const provider = providerMap[record.provider_id];
        if (!provider) {
          return; // Skip if we don't have provider data
        }

        // Check if date is in blocked periods
        if (this._isDateBlocked(dateString, record.blocked_periods)) {
          logger.debug('ProviderAvailabilityService: Provider has blocked period', {
            providerId: record.provider_id,
            date: dateString
          });
          return;
        }

        // Get available time slots for the day of week
        const weeklyAvailability = record.weekly_availability || {};
        const daySlots = weeklyAvailability[dayOfWeek] || [];

        if (!daySlots || daySlots.length === 0) {
          logger.debug('ProviderAvailabilityService: No availability for day', {
            providerId: record.provider_id,
            dayOfWeek
          });
          return;
        }

        // Filter out slots that conflict with existing meetings
        const providerMeetings = meetingsByProvider[record.provider_id] || [];
        const availableSlots = daySlots.filter(slot => {
          return !this._hasTimeConflict(slot.startTime, slot.endTime, providerMeetings);
        });

        if (availableSlots.length === 0) {
          logger.debug('ProviderAvailabilityService: All slots booked', {
            providerId: record.provider_id
          });
          return;
        }

        // Format location
        let formattedLocation = null;
        if (provider.location) {
          if (typeof provider.location === 'object') {
            const loc = provider.location;
            formattedLocation = [loc.city, loc.state].filter(Boolean).join(', ');
          } else if (typeof provider.location === 'string') {
            formattedLocation = provider.location;
          }
        }

        // Provider has availability!
        availableProviders.push({
          providerId: record.provider_id,
          providerName: `${provider.first_name} ${provider.last_name}`.trim(),
          profilePicture: provider.profile_picture,
          specialty: provider.specialty,
          location: formattedLocation, // General provider location (from user_profiles)
          availableSlots: availableSlots.map(slot => {
            // Get location data for this specific time slot
            const slotLocation = slot.location_id ? locationMap[slot.location_id] : null;

            return {
              id: slot.id,
              startTime: slot.startTime,
              endTime: slot.endTime,
              label: slot.label || this._formatTimeLabel(slot.startTime, slot.endTime),
              displayLabel: this._formatSlotDisplayLabel(slot, slotLocation),
              notes: slot.notes,
              // Location-specific data for this time slot
              location_id: slot.location_id || null,
              locationData: slotLocation ? {
                id: slotLocation.id,
                name: slotLocation.location_name,
                address: slotLocation.address,
                city: slotLocation.city,
                state: slotLocation.state,
                type: slotLocation.location_type
              } : null
            };
          }),
          meetingPreferences: record.meeting_preferences
        });
      });

      logger.debug('ProviderAvailabilityService: Found available providers', {
        count: availableProviders.length
      });

      return availableProviders;
    } catch (error) {
      logger.error('ProviderAvailabilityService: Error checking availability', {
        error: error.message
      });
      return [];
    }
  },

  /**
   * Check availability for a single provider
   * @param {string} providerId - Provider UUID
   * @param {Date|string} targetDate - The date to check
   * @returns {Promise<Object|null>} Provider availability or null
   */
  async checkProviderAvailability(providerId, targetDate) {
    const results = await this.checkProvidersAvailability([providerId], targetDate);
    return results.length > 0 ? results[0] : null;
  },

  /**
   * Helper: Get day of week key (monday, tuesday, etc.)
   */
  _getDayOfWeekKey(date) {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days[date.getDay()];
  },

  /**
   * Helper: Format date as YYYY-MM-DD
   */
  _formatDateString(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  },

  /**
   * Helper: Check if date is in blocked periods
   */
  _isDateBlocked(dateString, blockedPeriods) {
    if (!blockedPeriods || blockedPeriods.length === 0) {
      return false;
    }

    const checkDate = new Date(dateString);

    return blockedPeriods.some(period => {
      const startDate = new Date(period.startDate);
      const endDate = new Date(period.endDate);
      return checkDate >= startDate && checkDate <= endDate;
    });
  },

  /**
   * Helper: Check if a time slot conflicts with existing meetings
   */
  _hasTimeConflict(slotStart, slotEnd, existingMeetings) {
    if (!existingMeetings || existingMeetings.length === 0) {
      return false;
    }

    return existingMeetings.some(meeting => {
      // Check if there's any overlap
      return (slotStart < meeting.endTime && slotEnd > meeting.startTime);
    });
  },

  /**
   * Helper: Format time label
   */
  _formatTimeLabel(startTime, endTime) {
    const formatTime = (time) => {
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${displayHour}:${minutes} ${ampm}`;
    };

    return `${formatTime(startTime)} - ${formatTime(endTime)}`;
  },

  /**
   * Helper: Format slot display label with time only (no location)
   * Examples:
   *   - "Lunch 12-1pm"
   *   - "Morning 9-10am"
   *   - "Afternoon 3-4pm"
   */
  _formatSlotDisplayLabel(slot, locationData) {
    const formatCompactTime = (time) => {
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours);
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      // Only include minutes if not :00
      return minutes === '00' ? displayHour : `${displayHour}:${minutes}`;
    };

    const startFormatted = formatCompactTime(slot.startTime);
    const endFormatted = formatCompactTime(slot.endTime);

    // Determine AM/PM
    const startHour = parseInt(slot.startTime.split(':')[0]);
    const endHour = parseInt(slot.endTime.split(':')[0]);
    const endAmPm = endHour >= 12 ? 'pm' : 'am';

    // Build the time portion
    let timeLabel = '';
    if (slot.label) {
      timeLabel = `${slot.label} ${startFormatted}-${endFormatted}${endAmPm}`;
    } else {
      timeLabel = `${startFormatted}-${endFormatted}${endAmPm}`;
    }

    return timeLabel;
  }
};

export default providerAvailabilityService;
